import {
  type Calendar,
  type CalendarEvent,
  CapacitorCalendar as CapacitorCalendarPlugin,
  type CreateEventOptions,
  type ModifyEventOptions,
} from "@ebarooni/capacitor-calendar";
import { Capacitor } from "@/lib/capacitor";
import { logger } from "@/logger";

/**
 * De agenda van het toestel.
 *
 * Op web bestaat er geen agenda om in te schrijven; alles geeft daar een lege uitkomst in plaats
 * van een crash, zodat de instellingenpagina in de browser gewoon te openen is.
 */
export const DeviceCalendar = (function () {
  const calendarLogger = logger.getLogger("@ebarooni/capacitor-calendar");

  function isAvailable(): boolean {
    return Capacitor.getPlatform() !== "web";
  }

  /**
   * Volledige toegang, niet alleen schrijven: we moeten onze eigen afspraken kunnen terugvinden om
   * ze bij te werken en op te ruimen. Met write-only zou elke synchronisatie duplicaten maken.
   */
  async function requestAccess(): Promise<boolean> {
    if (!isAvailable()) {
      calendarLogger.trace("DeviceCalendar is not available on web");
      return false;
    }

    const { result } = await CapacitorCalendarPlugin.requestFullCalendarAccess();
    return result === "granted";
  }

  async function hasAccess(): Promise<boolean> {
    if (!isAvailable()) {
      return false;
    }

    const { result } = await CapacitorCalendarPlugin.checkAllPermissions();
    return result.readCalendar === "granted" && result.writeCalendar === "granted";
  }

  /**
   * Alleen agenda's waar we ook echt in mogen schrijven. Een abonnement (feestdagen, een gedeelde
   * agenda van iemand anders) staat wel in de lijst van het toestel, maar weigert elke afspraak —
   * die aanbieden levert alleen een foutmelding op het moment dat het al te laat is.
   */
  async function listWritableCalendars(): Promise<Calendar[]> {
    if (!isAvailable()) {
      return [];
    }

    const { result } = await CapacitorCalendarPlugin.listCalendars();
    return result.filter(
      (calendar) => calendar.allowsContentModifications !== false && calendar.isSubscribed !== true,
    );
  }

  async function listEventsInRange(from: number, to: number): Promise<CalendarEvent[]> {
    if (!isAvailable()) {
      return [];
    }

    const { result } = await CapacitorCalendarPlugin.listEventsInRange({ from, to });
    return result;
  }

  async function createEvent(options: CreateEventOptions): Promise<string> {
    const { id } = await CapacitorCalendarPlugin.createEvent(options);
    return id;
  }

  async function modifyEvent(options: ModifyEventOptions): Promise<void> {
    await CapacitorCalendarPlugin.modifyEvent(options);
  }

  async function deleteEvent(id: string): Promise<void> {
    await CapacitorCalendarPlugin.deleteEvent({ id });
  }

  return {
    isAvailable,
    requestAccess,
    hasAccess,
    listWritableCalendars,
    listEventsInRange,
    createEvent,
    modifyEvent,
    deleteEvent,
  };
})();

export type { Calendar, CalendarEvent };
